import { createIsomorphicFn } from "@tanstack/react-start";
import { getRequestHeaders } from "@tanstack/react-start/server";

const JSON_CONTENT_TYPE = "application/json";
const SSR_REQUEST_HEADER_ALLOWLIST = new Set([
  "cookie",
  "referer",
  "user-agent",
  "x-forwarded-for",
  "x-forwarded-host",
  "x-forwarded-port",
  "x-forwarded-proto",
  "x-real-ip",
]);

export class ApiClientError<TBody = unknown> extends Error {
  readonly status: number;
  readonly body: TBody;
  readonly code?: string;
  readonly response: Response;

  constructor(response: Response, body: TBody) {
    super(readApiErrorMessage(response, body));
    this.name = "ApiClientError";
    this.status = response.status;
    this.body = body;
    this.code = readApiErrorCode(body);
    this.response = response;
  }
}

function readApiErrorMessage(response: Response, body: unknown) {
  if (typeof body === "string" && body.trim()) {
    return body;
  }

  if (
    body &&
    typeof body === "object" &&
    "message" in body &&
    typeof body.message === "string" &&
    body.message.trim()
  ) {
    return body.message;
  }

  return `请求失败（HTTP ${response.status}）`;
}

function readApiErrorCode(body: unknown) {
  if (
    body &&
    typeof body === "object" &&
    "code" in body &&
    typeof body.code === "string" &&
    body.code.trim()
  ) {
    return body.code;
  }

  return undefined;
}

function apiBaseUrl() {
  if (!import.meta.env.SSR) {
    return "";
  }

  return (process.env.API_INTERNAL_BASE_URL || "http://127.0.0.1:3000").replace(/\/+$/, "");
}

function resolveApiUrl(url: string) {
  if (/^https?:\/\//.test(url)) {
    return url;
  }

  const baseUrl = apiBaseUrl();
  if (!baseUrl) {
    return url;
  }

  return `${baseUrl}${url.startsWith("/") ? url : `/${url}`}`;
}

function shouldSendJsonContentType(body: BodyInit | null | undefined) {
  return (
    body !== undefined &&
    body !== null &&
    !(body instanceof Blob) &&
    !(body instanceof FormData) &&
    !(body instanceof URLSearchParams)
  );
}

function shouldForwardSsrRequestHeader(name: string) {
  const normalizedName = name.toLowerCase();
  return normalizedName.startsWith("eo-") || SSR_REQUEST_HEADER_ALLOWLIST.has(normalizedName);
}

export function applyIncomingSsrRequestHeaders(
  headers: Headers,
  incomingHeaders: Iterable<[string, string]>,
) {
  for (const [name, value] of incomingHeaders) {
    if (!shouldForwardSsrRequestHeader(name) || headers.has(name)) {
      continue;
    }

    headers.set(name, value);
  }
}

async function readResponseBody<T>(response: Response): Promise<T | undefined> {
  if ([204, 205, 304].includes(response.status)) {
    return undefined;
  }

  const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
  if (contentType.includes("json")) {
    return (await response.json()) as T;
  }

  if (contentType.startsWith("text/")) {
    return (await response.text()) as T;
  }

  return (await response.blob()) as T;
}

const getSsrRequestHeaderEntries: () => Promise<Array<[string, string]>> = createIsomorphicFn()
  .server(async () =>
    Array.from(getRequestHeaders()).filter(([name]) => shouldForwardSsrRequestHeader(name)),
  )
  .client(async () => []);

async function applySsrRequestHeaders(headers: Headers) {
  if (!import.meta.env.SSR) {
    return;
  }

  applyIncomingSsrRequestHeaders(headers, await getSsrRequestHeaderEntries());
}

export async function cnapexApiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await cnapexApiRawFetch(url, options);
  return (await readResponseBody<T>(response)) as T;
}

export async function cnapexApiRawFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const headers = new Headers(options.headers);

  if (!headers.has("Accept")) {
    headers.set("Accept", JSON_CONTENT_TYPE);
  }

  if (!headers.has("Content-Type") && shouldSendJsonContentType(options.body)) {
    headers.set("Content-Type", JSON_CONTENT_TYPE);
  }

  await applySsrRequestHeaders(headers);

  const response = await fetch(resolveApiUrl(url), {
    ...options,
    credentials: options.credentials ?? "include",
    headers,
  });

  if (!response.ok) {
    const body = await readResponseBody(response);
    throw new ApiClientError(response, body);
  }

  return response;
}
